// src/stores/data.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiService } from '@/services/api'

export const useDataStore = defineStore('data', () => {
  // State
  const datasets = ref([])
  const systemHealth = ref({})
  const fraudIndicators = ref([])
  const churnFeatures = ref([])
  const atRiskStudents = ref([])
  const financialData = ref([])
  const learningData = ref([])
  const isLoading = ref(false)
  const lastUpdated = ref(null)

  // Getters
  const totalSchools = computed(() => {
    return datasets.value.reduce((max, dataset) => 
      Math.max(max, dataset.schools_count || 0), 0
    )
  })

  const totalRecords = computed(() => {
    return datasets.value.reduce((sum, dataset) => 
      sum + (dataset.total_records || 0), 0
    )
  })

  const dataFreshness = computed(() => {
    if (!lastUpdated.value) return 'No data'
    const now = new Date()
    const diff = now - new Date(lastUpdated.value)
    const hours = Math.floor(diff / (1000 * 60 * 60))
    
    if (hours < 1) return 'Just now'
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
  })

  const flowStats = computed(() => {
    const fraudCount = fraudIndicators.value.filter(f => f.is_fraud).length
    const churnRiskHigh = churnFeatures.value.filter(c => 
      ['HIGH', 'CRITICAL'].includes(c.churn_risk_category)
    ).length
    const activeSchools = new Set([
      ...fraudIndicators.value.map(f => f.school_id),
      ...churnFeatures.value.map(c => c.school_id)
    ]).size

    return { fraudCount, churnRiskHigh, activeSchools }
  })

  const analyticsStats = computed(() => {
    const atRiskCount = atRiskStudents.value.filter(s => s.at_risk_flag).length
    const totalStudents = atRiskStudents.value.length
    const avgPerformance = totalStudents > 0 
      ? Math.round(atRiskStudents.value.reduce((sum, s) => 
          sum + (s.grade_avg_current_term || 0), 0) / totalStudents)
      : 0

    return { atRiskStudents: atRiskCount, totalStudents, avgPerformance }
  })

  // Actions
  const loadInitialData = async () => {
    isLoading.value = true
    try {
      await Promise.all([
        loadSystemHealth(),
        loadDatasets(),
        loadFlowData(),
        loadAnalyticsData()
      ])
      lastUpdated.value = new Date().toISOString()
    } catch (error) {
      console.error('Failed to load initial data:', error)
    } finally {
      isLoading.value = false
    }
  }

  const loadSystemHealth = async () => {
    try {
      const response = await apiService.getSystemHealth()
      systemHealth.value = response.data
    } catch (error) {
      console.error('Failed to load system health:', error)
      systemHealth.value = { status: 'error' }
    }
  }

  const loadDatasets = async () => {
    try {
      const response = await apiService.getDatasets()
      datasets.value = response.data
    } catch (error) {
      console.error('Failed to load datasets:', error)
      datasets.value = []
    }
  }

  const loadFlowData = async () => {
    try {
      const [fraudResp, churnResp] = await Promise.all([
        apiService.getFraudIndicators({ limit: 100 }),
        apiService.getChurnFeatures({ limit: 100 })
      ])
      
      fraudIndicators.value = fraudResp.data.data || []
      churnFeatures.value = churnResp.data.data || []
    } catch (error) {
      console.error('Failed to load Flow data:', error)
      fraudIndicators.value = []
      churnFeatures.value = []
    }
  }

  const loadAnalyticsData = async () => {
    try {
      const response = await apiService.getAtRiskStudents({ limit: 100 })
      atRiskStudents.value = response.data.data || []
    } catch (error) {
      console.error('Failed to load Analytics data:', error)
      atRiskStudents.value = []
    }
  }

  const refreshData = async () => {
    await loadInitialData()
  }

  const clearData = () => {
    datasets.value = []
    systemHealth.value = {}
    fraudIndicators.value = []
    churnFeatures.value = []
    atRiskStudents.value = []
    financialData.value = []
    learningData.value = []
    lastUpdated.value = null
  }

  return {
    // State
    datasets,
    systemHealth,
    fraudIndicators,
    churnFeatures,
    atRiskStudents,
    financialData,
    learningData,
    isLoading,
    lastUpdated,
    
    // Getters
    totalSchools,
    totalRecords,
    dataFreshness,
    flowStats,
    analyticsStats,
    
    // Actions
    loadInitialData,
    loadSystemHealth,
    loadDatasets,
    loadFlowData,
    loadAnalyticsData,
    refreshData,
    clearData
  }
})
